import { SITE } from "@/content/site";
import { BN_LANG_META, bnKeywords } from "@/content/bn/keywords";
import { bnHreflangLinks } from "@/lib/seo";

/** One `<meta>` entry as accepted by a TanStack route `head()`. */
type MetaTag = Record<string, string | undefined>;

/** Brand suffix appended to Bengali document titles. */
export const BN_TITLE_SUFFIX = " | ফ্রিল্যান্সার তামাল";

export interface BnPageHeadOptions {
  /** Path of the Bengali page, e.g. "/bn/pricing". Used for og:url and canonical. */
  path: string;
  /** Matching English path; omit when the page has no English counterpart. */
  enPath?: string;
  /** Social/heading title. The document title adds the brand suffix unless `suffix` is false. */
  title: string;
  desc: string;
  /** Page-specific keyword phrases; the shared Bengali base set is appended automatically. */
  keywords: (string | string[] | undefined)[];
  /** og:type — defaults to "website". */
  type?: string;
  /** Set false when `title` already carries the brand name. */
  suffix?: boolean;
  /** Extra tags placed right after the shared language tags (author, geo, …). */
  meta?: MetaTag[];
  /** Social share image; emits og:image, og:image:alt and twitter:image. */
  image?: { url: string; alt: string };
  /** Extra Open Graph tags placed after og:type (article dates, profile fields, …). */
  og?: MetaTag[];
  /** Emit twitter:card. Defaults to true. */
  twitterCard?: boolean;
  /** Tags appended at the end (e.g. robots for drafts). */
  tail?: MetaTag[];
}

/**
 * Head metadata for a Bengali page: title, description, keywords, language tags,
 * Open Graph / Twitter tags, canonical and hreflang alternates — in one consistent order.
 */
export function bnPageHead(o: BnPageHeadOptions) {
  const url = `${SITE.url}${o.path}`;
  const meta: MetaTag[] = [
    { title: o.suffix === false ? o.title : `${o.title}${BN_TITLE_SUFFIX}` },
    { name: "description", content: o.desc },
    { name: "keywords", content: bnKeywords(...o.keywords) },
    ...BN_LANG_META,
    ...(o.meta ?? []),
    { property: "og:title", content: o.title },
    { property: "og:description", content: o.desc },
    { property: "og:url", content: url },
    { property: "og:type", content: o.type ?? "website" },
    ...(o.image
      ? [
          { property: "og:image", content: o.image.url },
          { property: "og:image:alt", content: o.image.alt },
        ]
      : []),
    ...(o.og ?? []),
    ...(o.twitterCard === false ? [] : [{ name: "twitter:card", content: "summary_large_image" }]),
    { name: "twitter:title", content: o.title },
    { name: "twitter:description", content: o.desc },
    ...(o.image ? [{ name: "twitter:image", content: o.image.url }] : []),
    ...(o.tail ?? []),
  ];
  return { meta, links: bnHreflangLinks(o.path, o.enPath) };
}
